import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  MenuItem,
  InputAdornment,
  Chip,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Grid,
  Divider,
  Paper,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Schedule as PendingIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
  FlightTakeoff as TravelIcon,
  AttachMoney as MoneyIcon,
} from "@mui/icons-material";
import { format, differenceInCalendarDays } from "date-fns";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/slices/authSlice";
import { useAuth } from "../../components/features/auth/AuthGuard";
import { ROUTES, ROLES } from "../../constants";
import { travelAPI } from "../../services/api/travel.api";
import useNotification from "../../hooks/common/useNotification";
import { LoadingSpinner } from "../../components/common/Loading";

const TravelRequestApprovalPage = () => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const { hasRole } = useAuth();
  const { showSuccess, showError } = useNotification();

  const [travelRequests, setTravelRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [viewMode, setViewMode] = useState("pending");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalAction, setApprovalAction] = useState(null);
  const [approvalComment, setApprovalComment] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 10,
    total: 0,
  });
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Debounce search so we don't fetch on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    fetchPendingRequests();
  }, [
    pagination.page,
    pagination.rowsPerPage,
    viewMode,
    statusFilter,
    debouncedSearchTerm,
  ]);

  // Reset to first page when filters change (so status/search change doesn't leave us on page 2+)
  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 0 }));
  }, [statusFilter, debouncedSearchTerm]);

  useEffect(() => {
    setStatusFilter("");
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setPagination((prev) => ({ ...prev, page: 0 }));
  }, [viewMode]);

  /** Format request type for display (backend may return requestType or request_type object/code). */
  const getRequestTypeLabel = (request) => {
    if (!request) return "N/A";
    const typeObj = request.requestType ?? request.request_type;
    const name = typeObj?.name ?? typeObj?.code;
    if (name && typeof name === "string") return name;
    const code = typeof request.request_type === "string" ? request.request_type : null;
    if (code) return code.charAt(0).toUpperCase() + code.slice(1).replace(/_/g, " ");
    return "N/A";
  };

  /** Duration in days from start_date to end_date (inclusive). */
  const getDurationDays = (request) => {
    if (!request) return null;
    const start = request.start_date || request.departure_date;
    const end = request.end_date || request.return_date;
    if (!start || !end) return null;
    const days = differenceInCalendarDays(new Date(end), new Date(start)) + 1;
    return days >= 0 ? days : null;
  };

  /** Hide Approve/Reject when this user already approved (e.g. dept head approved -> approved_by_supervisor; don't show button again). */
  const canUserApproveRequest = (request) => {
    if (!request || !user) return false;
    const isSameUser = (a, b) => a != null && b != null && (Number(a) === Number(b) || String(a) === String(b));
    if (request.approval_status === "approved_by_supervisor" && isSameUser(request.approver_id, user.id)) return false;
    return request.approval_status === "pending" || request.approval_status === "approved_by_supervisor";
  };

  const fetchPendingRequests = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page + 1,
        limit: pagination.rowsPerPage,
      };

      if (hasRole(ROLES.ADMIN) && !statusFilter) {
        if (viewMode === "pending") {
          params.status = "pending,approved_by_supervisor";
        } else if (viewMode === "approved") {
          params.status = "approved,rejected";
        }
      } else if (hasRole(ROLES.HR_MANAGER) && !statusFilter) {
        params.status = "approved_by_supervisor";
      } else if (statusFilter) {
        params.status = statusFilter;
      }

      if (debouncedSearchTerm && debouncedSearchTerm.trim()) {
        params.search = debouncedSearchTerm.trim();
      }

      const response = await travelAPI.getTravelRequestApprovalQueue(params);
      const body = response?.data;
      // Backend returns { success, message, data: { requests, pagination } }
      const data = body?.data ?? body;
      const requestsData = data?.requests ?? body?.requests ?? [];
      const paginationData = data?.pagination ?? body?.pagination ?? {};
      const success = body?.success !== false;

      setTravelRequests(Array.isArray(requestsData) ? requestsData : []);
      setPagination((prev) => ({
        ...prev,
        total: paginationData.total != null ? Number(paginationData.total) : prev.total,
      }));
    } catch (error) {
      console.error("Error fetching travel requests for approval:", error);
      showError("Failed to fetch travel requests");
      setTravelRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setApprovalAction("approve");
    setApprovalComment("");
    setApprovalDialogOpen(true);
    setAnchorEl(null);
  };

  const handleReject = (request) => {
    setSelectedRequest(request);
    setApprovalAction("reject");
    setRejectionReason("");
    setApprovalDialogOpen(true);
    setAnchorEl(null);
  };

  const handleConfirmAction = async () => {
    if (!selectedRequest) return;

    try {
      setActionLoading(true);

      if (approvalAction === "approve") {
        await travelAPI.approveTravelRequest(selectedRequest.id, {
          approval_comments: approvalComment,
        });
        showSuccess("Travel request approved successfully");
      } else if (approvalAction === "reject") {
        if (!rejectionReason.trim()) {
          showError("Please provide a rejection reason");
          return;
        }
        await travelAPI.rejectTravelRequest(selectedRequest.id, {
          rejection_reason: rejectionReason,
        });
        showSuccess("Travel request rejected");
      }

      setApprovalDialogOpen(false);
      setSelectedRequest(null);
      setApprovalComment("");
      setRejectionReason("");
      fetchPendingRequests();
    } catch (error) {
      console.error("Error processing approval:", error);
      showError(
        `Failed to ${approvalAction} travel request: ${error.response?.data?.message || error.message}`
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setExpandedRequest(request.id === expandedRequest ? null : request.id);
    setAnchorEl(null);
  };

  const getStatusColor = (status) => {
    const statusColors = {
      draft: "default",
      pending: "warning",
      approved_by_supervisor: "info",
      approved: "success",
      rejected: "error",
      completed: "success",
      cancelled: "default",
    };
    return statusColors[status] || "default";
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      draft: "Draft",
      pending: "Pending Approval",
      approved_by_supervisor: "Approved by Supervisor",
      approved: "Approved",
      rejected: "Rejected",
      completed: "Completed",
      cancelled: "Cancelled",
    };
    return statusLabels[status] || status;
  };

  const filteredRequests = travelRequests.filter((request) => {
    const searchLower = searchTerm.trim().toLowerCase();
    const matchesSearch =
      !searchLower ||
      (request.destination || "").toLowerCase().includes(searchLower) ||
      (request.purpose || "").toLowerCase().includes(searchLower) ||
      (request.user?.first_name || "").toLowerCase().includes(searchLower) ||
      (request.user?.sur_name || "").toLowerCase().includes(searchLower);
    const status = request.approval_status || request.status;
    const matchesStatus =
      !statusFilter || status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 1 }}>
            Travel Request Approvals
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Review and approve travel requests
          </Typography>
        </Box>
        <Button
          startIcon={<RefreshIcon />}
          onClick={fetchPendingRequests}
          variant="outlined"
        >
          Refresh
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by destination, purpose, or employee..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                select
                label="View Mode"
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value)}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved/Rejected</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                select
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved_by_supervisor">Approved by Supervisor</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Travel Requests List */}
      {filteredRequests.length === 0 ? (
        <Card>
          <CardContent>
            <Alert severity="info">No travel requests found for approval</Alert>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {filteredRequests.map((request) => (
            <Card key={request.id} variant="outlined">
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                      <Typography variant="h6">
                        {request.destination || "No destination"}
                      </Typography>
                      <Chip
                        label={getStatusLabel(request.approval_status || request.status)}
                        color={getStatusColor(request.approval_status || request.status)}
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {request.purpose}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1 }}>
                      <Typography variant="body2">
                        <PersonIcon sx={{ verticalAlign: "middle", mr: 0.5, fontSize: 16 }} />
                        {request.user?.first_name} {request.user?.sur_name}
                      </Typography>
                      <Typography variant="body2">
                        <EventIcon sx={{ verticalAlign: "middle", mr: 0.5, fontSize: 16 }} />
                        {request.start_date ? format(new Date(request.start_date), "dd/MM/yyyy") : "-"} - {request.end_date ? format(new Date(request.end_date), "dd/MM/yyyy") : "-"}
                      </Typography>
                      <Typography variant="body2">
                        <MoneyIcon sx={{ verticalAlign: "middle", mr: 0.5, fontSize: 16 }} />
                        TZS {parseFloat(request.estimated_cost || 0).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                  <Box>
                    <IconButton
                      onClick={(e) => {
                        setAnchorEl(e.currentTarget);
                        setSelectedRequest(request);
                      }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>
                </Box>

                {expandedRequest === request.id && (
                  <Accordion expanded={true} sx={{ mt: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2">Request Details</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Request Type
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 2 }}>
                            {getRequestTypeLabel(request)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Duration
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 2 }}>
                            {getDurationDays(request) != null ? `${getDurationDays(request)} day(s)` : "N/A"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Transport Mode
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 2 }}>
                            {request.transport_mode ? request.transport_mode.charAt(0).toUpperCase() + request.transport_mode.slice(1) : "N/A"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Accommodation Required
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 2 }}>
                            {request.accommodation_required ? "Yes" : "No"}
                          </Typography>
                        </Grid>
                        {request.approval_comments && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              Approval Comments
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                              {request.approval_comments}
                            </Typography>
                          </Grid>
                        )}
                        {request.rejection_reason && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              Rejection Reason
                            </Typography>
                            <Typography variant="body1" color="error" sx={{ mb: 2 }}>
                              {request.rejection_reason}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                )}

                {(request.approval_status === "pending" || request.approval_status === "approved_by_supervisor") && (
                  <Box sx={{ display: "flex", gap: 1, mt: 2, justifyContent: "flex-end" }}>
                    <Button
                      variant="outlined"
                      startIcon={<ViewIcon />}
                      onClick={() => handleViewDetails(request)}
                    >
                      {expandedRequest === request.id ? "Hide" : "View"} Details
                    </Button>
                    {canUserApproveRequest(request) && (
                      <>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<ApproveIcon />}
                          onClick={() => handleApprove(request)}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={<RejectIcon />}
                          onClick={() => handleReject(request)}
                        >
                          Reject
                        </Button>
                      </>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Pagination */}
      {pagination.total > 0 && (
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page}
          onPageChange={(e, newPage) =>
            setPagination((prev) => ({ ...prev, page: newPage }))
          }
          rowsPerPage={pagination.rowsPerPage}
          onRowsPerPageChange={(e) =>
            setPagination((prev) => ({
              ...prev,
              rowsPerPage: parseInt(e.target.value, 10),
              page: 0,
            }))
          }
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      )}

      {/* Approval/Rejection Dialog */}
      <Dialog
        open={approvalDialogOpen}
        onClose={() => {
          if (!actionLoading) {
            setApprovalDialogOpen(false);
            setSelectedRequest(null);
            setApprovalComment("");
            setRejectionReason("");
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {approvalAction === "approve" ? "Approve Travel Request" : "Reject Travel Request"}
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Destination: {selectedRequest.destination}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Employee: {selectedRequest.user?.first_name} {selectedRequest.user?.sur_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Type: {getRequestTypeLabel(selectedRequest)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Duration: {getDurationDays(selectedRequest) != null ? `${getDurationDays(selectedRequest)} day(s)` : "N/A"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Dates: {selectedRequest.start_date ? format(new Date(selectedRequest.start_date), "dd/MM/yyyy") : "-"} - {selectedRequest.end_date ? format(new Date(selectedRequest.end_date), "dd/MM/yyyy") : "-"}
              </Typography>
            </Box>
          )}
          {approvalAction === "approve" ? (
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Approval Comments (Optional)"
              value={approvalComment}
              onChange={(e) => setApprovalComment(e.target.value)}
              placeholder="Add any comments about this approval..."
            />
          ) : (
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Rejection Reason *"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Please provide a reason for rejection..."
              required
              error={!rejectionReason.trim()}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setApprovalDialogOpen(false);
              setSelectedRequest(null);
              setApprovalComment("");
              setRejectionReason("");
            }}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color={approvalAction === "approve" ? "success" : "error"}
            disabled={actionLoading || (approvalAction === "reject" && !rejectionReason.trim())}
          >
            {actionLoading ? (
              <CircularProgress size={20} />
            ) : approvalAction === "approve" ? (
              "Approve"
            ) : (
              "Reject"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => handleViewDetails(selectedRequest)}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        {selectedRequest && canUserApproveRequest(selectedRequest) && (
          <>
            <MenuItem onClick={() => handleApprove(selectedRequest)}>
              <ListItemIcon>
                <ApproveIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText>Approve</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleReject(selectedRequest)}>
              <ListItemIcon>
                <RejectIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Reject</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};

export default TravelRequestApprovalPage;
